// AllPassRecordsPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import DataTable from './common/DataTable';
import {
    ShieldAlert,
    RefreshCw,
    Download,
    Filter,
    Calendar,
    ArrowRightCircle,
    ArrowLeftCircle,
    Cpu,
    Edit3,
    AlertTriangle,
    Layers,
    X,
    Building2,
    Radio
} from 'lucide-react';

const getEntrySourceMeta = (source) => {
    switch (source) {
        case 'Manual':
        case 0:
            return { label: 'Manuel', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200 border border-blue-200 dark:border-blue-800' };
        case 'QrScan':
        case 1:
            return { label: 'QR Tarama', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200 border border-purple-200 dark:border-purple-800' };
        case 'Declared':
        case 3:
            return { label: 'Beyanlı Geçiş', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800' };
        case 'Bluetooth':
        case 4:
            return { label: 'Bluetooth', className: 'bg-sky-500 text-white' };
        case 'Device':
        case 2:
        default:
            return { label: 'Cihaz / Okuyucu', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200 border border-amber-200 dark:border-amber-800' };
    }
};

const getOlusturanColorClass = (olusturan, kaynak) => {
    switch (kaynak) {
        case 'Bluetooth':
        case 4:
            return 'bg-sky-500 text-white';
        case 'Device':
        case 2:
            return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
        case 'QrScan':
        case 1:
            return 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200';
        case 'Manual':
        case 0:
            if (olusturan && olusturan.toLowerCase() !== 'sistem/cihaz' && olusturan.toLowerCase() !== 'bilinmiyor') {
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
            }
            return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
        default:
            return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
};

const toDateInputValue = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export default function AllPassRecordsPage() {
    const [allRecords, setAllRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { permissions } = useAuth();
    const canView = useMemo(() => permissions.includes('pass_records:view_list'), [permissions]);

    // Lookups (Departments + AccessPoints/Slots)
    const [departments, setDepartments] = useState([]);
    const [accessPoints, setAccessPoints] = useState([]);

    // Filters
    const [datePreset, setDatePreset] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [departmentId, setDepartmentId] = useState('');
    const [deviceFilter, setDeviceFilter] = useState('');
    const [recordTypeFilter, setRecordTypeFilter] = useState('');
    const [entrySourceFilter, setEntrySourceFilter] = useState('');
    const [ignoreFilter, setIgnoreFilter] = useState('all');

    // Server-side table state
    const [serverParams, setServerParams] = useState({
        page: 1,
        itemsPerPage: 20,
        searchTerm: '',
        columnFilters: {},
        sortColumn: 'recordTime',
        sortDirection: 'desc'
    });

    const [totalRows, setTotalRows] = useState(0);
    const [summary, setSummary] = useState({
        totalCount: 0,
        entryCount: 0,
        exitCount: 0,
        deviceCount: 0,
        manualCount: 0,
        ignoredCount: 0
    });

    // Fetch lookups once
    useEffect(() => {
        if (!canView) return;
        let isMounted = true;
        apiClient('passrecord/form-lookups')
            .then((res) => {
                if (!isMounted) return;
                if (res?.departments) setDepartments(res.departments);
                if (res?.accessPoints) {
                    setAccessPoints(
                        res.accessPoints.map((ap) => ({
                            ...ap,
                            value: ap.value ?? ap.serialValue ?? ap.rawDeviceSerial ?? ap.name,
                            label: ap.label ?? ap.displayLabel ?? ap.name
                        }))
                    );
                }
            })
            .catch(() => {
                // Non-fatal if lookups fail
            });
        return () => {
            isMounted = false;
        };
    }, [canView]);

    const applyDatePreset = (preset) => {
        setDatePreset(preset);
        const now = new Date();
        if (preset === 'today') {
            const todayStr = toDateInputValue(now);
            setStartDate(todayStr);
            setEndDate(todayStr);
        } else if (preset === 'yesterday') {
            const y = new Date(now);
            y.setDate(y.getDate() - 1);
            const yStr = toDateInputValue(y);
            setStartDate(yStr);
            setEndDate(yStr);
        } else if (preset === 'last7') {
            const start = new Date(now);
            start.setDate(start.getDate() - 6);
            setStartDate(toDateInputValue(start));
            setEndDate(toDateInputValue(now));
        } else if (preset === 'thisMonth') {
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            setStartDate(toDateInputValue(start));
            setEndDate(toDateInputValue(now));
        } else {
            setStartDate('');
            setEndDate('');
        }
    };

    const resetTopFilters = () => {
        setDatePreset('all');
        setStartDate('');
        setEndDate('');
        setDepartmentId('');
        setDeviceFilter('');
        setRecordTypeFilter('');
        setEntrySourceFilter('');
        setIgnoreFilter('all');
    };

    const hasActiveTopFilters = Boolean(
        startDate ||
        endDate ||
        departmentId ||
        deviceFilter ||
        recordTypeFilter ||
        entrySourceFilter ||
        (ignoreFilter && ignoreFilter !== 'all')
    );

    const fetchData = useCallback(async () => {
        if (!canView) {
            setError("Geçiş kayıtlarını görüntüleme yetkiniz yok.");
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);

        try {
            const colFilters = serverParams.columnFilters || {};
            const combinedSearchParts = [
                serverParams.searchTerm,
                colFilters.employeeFullName,
                colFilters.notes,
                colFilters.olusturanKullaniciAdi
            ].filter(Boolean);
            const effectiveSearch = combinedSearchParts.join(' ').trim();

            const effectiveDeviceFilter = deviceFilter || colFilters.deviceSerialNumber || '';

            const queryParams = new URLSearchParams({
                page: String(serverParams.page || 1),
                pageSize: String(serverParams.itemsPerPage || 20)
            });

            if (effectiveSearch) queryParams.set('searchTerm', effectiveSearch);
            if (startDate) queryParams.set('startDate', startDate);
            if (endDate) queryParams.set('endDate', endDate);
            if (departmentId) queryParams.set('departmentId', departmentId);
            if (effectiveDeviceFilter) queryParams.set('deviceFilter', effectiveDeviceFilter);
            if (recordTypeFilter) queryParams.set('recordType', recordTypeFilter);
            if (entrySourceFilter) queryParams.set('entrySource', entrySourceFilter);
            if (ignoreFilter && ignoreFilter !== 'all') queryParams.set('ignoreFilter', ignoreFilter);
            if (serverParams.sortColumn) queryParams.set('sortBy', serverParams.sortColumn);
            if (serverParams.sortDirection) queryParams.set('sortDir', serverParams.sortDirection);

            const response = await apiClient(`passrecord?${queryParams.toString()}`);
            const items = response?.items || [];
            const total = response?.totalCount ?? 0;

            setAllRecords(items);
            setTotalRows(total);
            setSummary({
                totalCount: total,
                entryCount: response?.entryCount ?? 0,
                exitCount: response?.exitCount ?? 0,
                deviceCount: response?.deviceCount ?? 0,
                manualCount: response?.manualCount ?? 0,
                ignoredCount: response?.ignoredCount ?? 0
            });
        } catch (err) {
            setError(err.message || "Tüm geçiş kayıtları yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, [
        canView,
        serverParams,
        startDate,
        endDate,
        departmentId,
        deviceFilter,
        recordTypeFilter,
        entrySourceFilter,
        ignoreFilter
    ]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleServerParamsChange = useCallback((params) => {
        setServerParams(params);
    }, []);

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const handleExportCsv = () => {
        if (!allRecords || allRecords.length === 0) return;
        const headers = [
            'Sicil No',
            'Personel Ad Soyad',
            'Departman',
            'Geçiş Zamanı',
            'Yön',
            'Durum',
            'Kaynak',
            'Geçiş Noktası / Okuyucu',
            'Cihaz Kodu',
            'Notlar / Gerekçe',
            'Oluşturan',
            'Kayıt Tarihi'
        ];
        const rows = allRecords.map((r) => {
            const isEntry = r.recordType === 'Entry' || r.recordType === 0;
            const sourceMeta = getEntrySourceMeta(r.entrySource);
            return [
                r.employeeRegistrationNumber || '',
                r.employeeFullName || '',
                r.departmentName || '',
                formatDate(r.recordTime),
                isEntry ? 'Giriş' : 'Çıkış',
                r.isIgnored ? `Yok Sayıldı (${r.ignoreReason || ''})` : 'Geçerli',
                sourceMeta.label,
                r.deviceName || r.deviceSerialNumber || '',
                r.deviceSerialNumber || '',
                r.notes || '',
                r.olusturanKullaniciAdi || '',
                formatDate(r.createdDate)
            ];
        });

        const csvContent =
            '\uFEFF' +
            [headers, ...rows]
                .map((row) =>
                    row
                        .map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`)
                        .join(';')
                )
                .join('\r\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `gecis-kayitlari-${toDateInputValue(new Date())}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const passRecordColumns = useMemo(() => [
        {
            header: 'Personel',
            accessor: 'employeeFullName',
            sortable: true,
            searchable: true,
            width: '22%',
            render: (row) => (
                <div className="flex flex-col">
                    <span className="font-semibold text-gray-900 dark:text-white">
                        {row.employeeFullName || '-'}
                    </span>
                    <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                        {row.employeeRegistrationNumber && (
                            <span className="text-[11px] font-mono px-1.5 py-0.2 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                #{row.employeeRegistrationNumber}
                            </span>
                        )}
                        {row.departmentName && (
                            <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                • {row.departmentName}
                            </span>
                        )}
                    </div>
                </div>
            )
        },
        {
            header: 'Geçiş Zamanı',
            accessor: 'recordTime',
            sortable: true,
            filterable: false,
            width: '14%',
            render: (row) => (
                <span className="font-mono text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-100">
                    {formatDate(row.recordTime)}
                </span>
            )
        },
        {
            header: 'Yön / Durum',
            accessor: 'recordType',
            sortable: true,
            filterable: false,
            width: '13%',
            render: (row) => {
                const isEntry = row.recordType === 'Entry' || row.recordType === 0;
                return (
                    <div className="flex flex-wrap items-center gap-1.5">
                        <span
                            className={`px-2.5 py-0.5 inline-flex items-center gap-1 text-xs font-semibold rounded-full ${
                                isEntry
                                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800'
                                    : 'bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-200 border border-rose-200 dark:border-rose-800'
                            } ${row.isIgnored ? 'line-through opacity-60' : ''}`}
                        >
                            {isEntry ? <ArrowRightCircle size={13} /> : <ArrowLeftCircle size={13} />}
                            {isEntry ? 'Giriş' : 'Çıkış'}
                        </span>
                        {row.isIgnored && (
                            <span
                                title={row.ignoreReason || 'Puantaj kalite kontrol tarafından yok sayıldı (kayıt yasal olarak korunmaktadır)'}
                                className="px-2 py-0.5 inline-flex text-[10px] font-bold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200 border border-amber-300 dark:border-amber-700"
                            >
                                YOK SAYILDI
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            header: 'Geçiş Noktası / Okuyucu',
            accessor: 'deviceSerialNumber',
            sortable: true,
            searchable: true,
            width: '20%',
            render: (row) => {
                const resolvedName = row.deviceName || row.deviceSerialNumber || '-';
                const showRaw = row.deviceName && row.deviceSerialNumber && row.deviceName !== row.deviceSerialNumber;
                return (
                    <div className="flex flex-col">
                        <span className="font-medium text-gray-800 dark:text-gray-100 flex items-center gap-1.5">
                            <Radio size={13} className="text-indigo-500 shrink-0" />
                            <span>{resolvedName}</span>
                        </span>
                        {showRaw && (
                            <span className="text-[11px] font-mono text-gray-400 dark:text-gray-500 mt-0.5">
                                {row.deviceSerialNumber}
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            header: 'Kaynak',
            accessor: 'entrySource',
            sortable: true,
            filterable: false,
            width: '10%',
            render: (row) => {
                const meta = getEntrySourceMeta(row.entrySource);
                return (
                    <span className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full ${meta.className}`}>
                        {meta.label}
                    </span>
                );
            }
        },
        {
            header: 'Notlar / Gerekçe',
            accessor: 'notes',
            sortable: true,
            searchable: true,
            width: '12%',
            render: (row) => (
                <div className="max-w-xs space-y-0.5">
                    {row.isIgnored && (
                        <div className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 whitespace-normal">
                            ⚠️ Yok Sayıldı: {row.ignoreReason || 'Kalite Kontrol Düzeltmesi'}
                        </div>
                    )}
                    {row.notes ? (
                        <div className="text-xs text-gray-600 dark:text-gray-300 truncate" title={row.notes}>
                            {row.notes}
                        </div>
                    ) : (!row.isIgnored ? '-' : null)}
                </div>
            )
        },
        {
            header: 'Oluşturan / Kayıt',
            accessor: 'olusturanKullaniciAdi',
            sortable: true,
            searchable: true,
            width: '9%',
            render: (row) => {
                const colorClass = getOlusturanColorClass(row.olusturanKullaniciAdi, row.entrySource);
                return (
                    <div className="flex flex-col items-start gap-0.5">
                        <span className={`px-2 py-0.5 inline-flex text-[11px] font-semibold rounded-full ${colorClass}`}>
                            {row.olusturanKullaniciAdi || '-'}
                        </span>
                        {row.createdDate && (
                            <span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">
                                {formatDate(row.createdDate)}
                            </span>
                        )}
                    </div>
                );
            }
        }
    ], []);

    if (!loading && !canView) {
        return (
            <div className="p-6 bg-red-50 dark:bg-red-900/10 rounded-lg shadow-md text-center">
                <ShieldAlert className="mx-auto h-12 w-12 text-red-400" />
                <h3 className="mt-2 text-lg font-medium text-red-800 dark:text-red-300">Yetki Hatası</h3>
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4 p-1">
            {/* Sayfa Başlığı ve Üst Aksiyonlar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-200/70 dark:border-gray-700/70">
                <div>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Layers className="text-blue-600 dark:text-blue-400" size={22} />
                        Tüm Geçiş Kayıtları
                    </h1>
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        Cihaz, panel okuyucu slotları, mobil QR, Bluetooth ve manuel eklenen tüm personel geçiş hareketleri
                    </p>
                </div>

                <div className="flex items-center gap-2 self-end sm:self-auto">
                    <button
                        type="button"
                        onClick={fetchData}
                        disabled={loading}
                        className="px-3.5 py-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw size={15} className={loading ? 'animate-spin text-blue-500' : ''} />
                        Yenile
                    </button>
                    <button
                        type="button"
                        onClick={handleExportCsv}
                        disabled={allRecords.length === 0}
                        className="px-3.5 py-2 text-xs sm:text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg flex items-center gap-1.5 shadow-sm transition-colors disabled:opacity-50"
                    >
                        <Download size={15} />
                        CSV / Excel İndir
                    </button>
                </div>
            </div>

            {/* KPI Özet Kartları (Tıklanabilir Hızlı Filtre) */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <button
                    type="button"
                    onClick={() => {
                        setRecordTypeFilter('');
                        setEntrySourceFilter('');
                        setIgnoreFilter('all');
                    }}
                    className={`text-left p-3.5 rounded-xl border transition-all ${
                        !recordTypeFilter && !entrySourceFilter && ignoreFilter === 'all'
                            ? 'bg-blue-50/80 dark:bg-blue-900/25 border-blue-400 dark:border-blue-600 shadow-sm'
                            : 'bg-white dark:bg-gray-800 border-gray-200/70 dark:border-gray-700/70 hover:border-blue-300'
                    }`}
                >
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>Toplam Kayıt</span>
                        <Layers size={15} className="text-blue-500" />
                    </div>
                    <div className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mt-1">
                        {summary.totalCount.toLocaleString('tr-TR')}
                    </div>
                </button>

                <button
                    type="button"
                    onClick={() => setRecordTypeFilter(prev => (prev === 'Entry' ? '' : 'Entry'))}
                    className={`text-left p-3.5 rounded-xl border transition-all ${
                        recordTypeFilter === 'Entry'
                            ? 'bg-emerald-50 dark:bg-emerald-900/25 border-emerald-400 dark:border-emerald-600 shadow-sm'
                            : 'bg-white dark:bg-gray-800 border-gray-200/70 dark:border-gray-700/70 hover:border-emerald-300'
                    }`}
                >
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>Giriş Hareketi</span>
                        <ArrowRightCircle size={15} className="text-emerald-500" />
                    </div>
                    <div className="text-lg sm:text-xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                        {summary.entryCount.toLocaleString('tr-TR')}
                    </div>
                </button>

                <button
                    type="button"
                    onClick={() => setRecordTypeFilter(prev => (prev === 'Exit' ? '' : 'Exit'))}
                    className={`text-left p-3.5 rounded-xl border transition-all ${
                        recordTypeFilter === 'Exit'
                            ? 'bg-rose-50 dark:bg-rose-900/25 border-rose-400 dark:border-rose-600 shadow-sm'
                            : 'bg-white dark:bg-gray-800 border-gray-200/70 dark:border-gray-700/70 hover:border-rose-300'
                    }`}
                >
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>Çıkış Hareketi</span>
                        <ArrowLeftCircle size={15} className="text-rose-500" />
                    </div>
                    <div className="text-lg sm:text-xl font-bold text-rose-600 dark:text-rose-400 mt-1">
                        {summary.exitCount.toLocaleString('tr-TR')}
                    </div>
                </button>

                <button
                    type="button"
                    onClick={() => setEntrySourceFilter(prev => (prev === 'Device' ? '' : 'Device'))}
                    className={`text-left p-3.5 rounded-xl border transition-all ${
                        entrySourceFilter === 'Device'
                            ? 'bg-amber-50 dark:bg-amber-900/25 border-amber-400 dark:border-amber-600 shadow-sm'
                            : 'bg-white dark:bg-gray-800 border-gray-200/70 dark:border-gray-700/70 hover:border-amber-300'
                    }`}
                >
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>Cihaz / Okuyucu</span>
                        <Cpu size={15} className="text-amber-500" />
                    </div>
                    <div className="text-lg sm:text-xl font-bold text-amber-600 dark:text-amber-400 mt-1">
                        {summary.deviceCount.toLocaleString('tr-TR')}
                    </div>
                </button>

                <button
                    type="button"
                    onClick={() => setEntrySourceFilter(prev => (prev === 'Manual' ? '' : 'Manual'))}
                    className={`text-left p-3.5 rounded-xl border transition-all ${
                        entrySourceFilter === 'Manual'
                            ? 'bg-indigo-50 dark:bg-indigo-900/25 border-indigo-400 dark:border-indigo-600 shadow-sm'
                            : 'bg-white dark:bg-gray-800 border-gray-200/70 dark:border-gray-700/70 hover:border-indigo-300'
                    }`}
                >
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>Manuel Kayıt</span>
                        <Edit3 size={15} className="text-indigo-500" />
                    </div>
                    <div className="text-lg sm:text-xl font-bold text-indigo-600 dark:text-indigo-400 mt-1">
                        {summary.manualCount.toLocaleString('tr-TR')}
                    </div>
                </button>

                <button
                    type="button"
                    onClick={() => setIgnoreFilter(prev => (prev === 'ignored' ? 'all' : 'ignored'))}
                    className={`text-left p-3.5 rounded-xl border transition-all ${
                        ignoreFilter === 'ignored'
                            ? 'bg-orange-50 dark:bg-orange-900/25 border-orange-400 dark:border-orange-600 shadow-sm'
                            : 'bg-white dark:bg-gray-800 border-gray-200/70 dark:border-gray-700/70 hover:border-orange-300'
                    }`}
                >
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>Puantaj Dışı</span>
                        <AlertTriangle size={15} className="text-orange-500" />
                    </div>
                    <div className="text-lg sm:text-xl font-bold text-orange-600 dark:text-orange-400 mt-1">
                        {summary.ignoredCount.toLocaleString('tr-TR')}
                    </div>
                </button>
            </div>

            {/* Gelişmiş Filtre Paneli */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200/70 dark:border-gray-700/70 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        <Filter size={14} className="text-blue-500" />
                        Hızlı Tarih & Detaylı Filtreler
                    </div>

                    <div className="flex flex-wrap items-center gap-1.5">
                        {[
                            { id: 'today', label: 'Bugün' },
                            { id: 'yesterday', label: 'Dün' },
                            { id: 'last7', label: 'Son 7 Gün' },
                            { id: 'thisMonth', label: 'Bu Ay' },
                            { id: 'all', label: 'Tüm Zamanlar' }
                        ].map((preset) => (
                            <button
                                key={preset.id}
                                type="button"
                                onClick={() => applyDatePreset(preset.id)}
                                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                                    datePreset === preset.id
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            >
                                {preset.label}
                            </button>
                        ))}

                        {hasActiveTopFilters && (
                            <button
                                type="button"
                                onClick={resetTopFilters}
                                className="ml-2 px-2.5 py-1 text-xs font-medium text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20 hover:bg-rose-100 dark:hover:bg-rose-900/40 rounded-md flex items-center gap-1"
                            >
                                <X size={13} /> Filtreleri Sıfırla
                            </button>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3 pt-1">
                    <div>
                        <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
                            <Calendar size={12} /> Başlangıç Tarihi
                        </label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => {
                                setStartDate(e.target.value);
                                setDatePreset('custom');
                            }}
                            className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
                            <Calendar size={12} /> Bitiş Tarihi
                        </label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => {
                                setEndDate(e.target.value);
                                setDatePreset('custom');
                            }}
                            className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
                            <Building2 size={12} /> Departman
                        </label>
                        <select
                            value={departmentId}
                            onChange={(e) => setDepartmentId(e.target.value)}
                            className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Tüm Departmanlar</option>
                            {departments.map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
                            <Radio size={12} /> Geçiş Noktası / Okuyucu
                        </label>
                        <select
                            value={deviceFilter}
                            onChange={(e) => setDeviceFilter(e.target.value)}
                            className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Tüm Okuyucu & Cihazlar</option>
                            {accessPoints.map((ap) => (
                                <option key={ap.key} value={ap.value}>
                                    {ap.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Geçiş Yönü
                        </label>
                        <select
                            value={recordTypeFilter}
                            onChange={(e) => setRecordTypeFilter(e.target.value)}
                            className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Tümü (Giriş & Çıkış)</option>
                            <option value="Entry">Sadece Giriş</option>
                            <option value="Exit">Sadece Çıkış</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Kayıt Kaynağı
                        </label>
                        <select
                            value={entrySourceFilter}
                            onChange={(e) => setEntrySourceFilter(e.target.value)}
                            className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Tüm Kaynaklar</option>
                            <option value="Device">Cihaz / Okuyucu</option>
                            <option value="Manual">Manuel</option>
                            <option value="QrScan">QR Tarama</option>
                            <option value="Declared">Beyanlı Geçiş</option>
                            <option value="Bluetooth">Bluetooth</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Puantaj Durumu
                        </label>
                        <select
                            value={ignoreFilter}
                            onChange={(e) => setIgnoreFilter(e.target.value)}
                            className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">Tümü</option>
                            <option value="active">Sadece Geçerli Kayıtlar</option>
                            <option value="ignored">Yok Sayılan (Puantaj Dışı)</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Veri Tablosu */}
            <DataTable
                data={allRecords}
                columns={passRecordColumns}
                itemsPerPage={serverParams.itemsPerPage}
                showSearch={true}
                searchPlaceholder="Personel adı, sicil no, departman, okuyucu adı veya notlarda ara..."
                showPagination={true}
                emptyMessage="Seçilen kriterlere uygun geçiş kaydı bulunamadı."
                isLoading={loading}
                errorMessage={error}
                serverSide={true}
                totalRows={totalRows}
                onServerParamsChange={handleServerParamsChange}
            />
        </div>
    );
}
